// components
import { SideEditorPanel } from '@components/SideEditor/SideEditor';
import { emailSignatureSideEditorServicesContext } from '@context/email-signature-services-context';
// hooks and contexts
import {
  formatEmailSignatureSocialLinksType,
  generateNewEmailSignatureSocialLinkId,
} from '@modules/email-signatures-helpers';
// es-toolkit/compat: lodash isEmpty treats 0/true/null as empty
import { isEmpty } from 'es-toolkit/compat';
import { createContext, useCallback, useContext, useMemo, useState } from 'react';

// consts and types
import { MenuItemsIndexDictionary } from '../../consts';
import {
  EmailSignatureSideEditorItems,
  type EmailSignatureSocialLinkId,
  EmailSignatureSocialLinkTypes,
} from '../../types';
import EmailSignatureEditorNewFieldButton from '../EmailSignatureEditorNewFieldButton';
import EmailSignatureEditorPanelNewFieldsSection, {
  EmailSignatureEditorPanelNewFieldsSectionHelper,
} from '../EmailSignatureEditorPanelNewFieldsSection';
import EmailSignatureLockLayer from '../EmailSignatureLockLayer';
import EmailSignatureEditorSocialLinksSection from './EmailSignatureEditorSocialLinksSection';

interface EmailSignatureEditorSocialPanelContextValues {
  isInSwapMode: boolean;
  currentFieldIdToSwap: EmailSignatureSocialLinkId | null;
  onSwapActivate: (fieldIdOfFieldToSwap: EmailSignatureSocialLinkId) => void;
}

export const EmailSignatureEditorSocialPanelContext =
  createContext<EmailSignatureEditorSocialPanelContextValues>({
    currentFieldIdToSwap: null,
    isInSwapMode: false,
    onSwapActivate: () => console.log('Warning! onSwapActivate is not implemented'),
  });

const NUM_SOCIAL_LINKS_RECOMMENDATION = 4;

const EmailSignatureSideEditorSocialPanel: React.FC<React.PropsWithChildren<unknown>> = () => {
  const { emailSignature, emailSignatureDispatch, isSignatureLocked } = useContext(
    emailSignatureSideEditorServicesContext,
  );
  const [currentFieldIdToSwap, setCurrentFieldIdToSwap] =
    useState<EmailSignatureSocialLinkId | null>(null);
  const isInSwapMode = useMemo(() => currentFieldIdToSwap !== null, [currentFieldIdToSwap]);
  const numSocialLinks = Object.keys(emailSignature.socialLinks).length;

  const onSwapActivate = (fieldIdOfFieldToSwap: EmailSignatureSocialLinkId) => {
    if (fieldIdOfFieldToSwap !== currentFieldIdToSwap && currentFieldIdToSwap !== null) {
      emailSignatureDispatch({
        type: 'SWAP_SOCIAL_LINKS',
        value: { fieldIdA: currentFieldIdToSwap, fieldIdB: fieldIdOfFieldToSwap },
      });
      setCurrentFieldIdToSwap(null);
    } else if (fieldIdOfFieldToSwap === currentFieldIdToSwap && currentFieldIdToSwap !== null) {
      setCurrentFieldIdToSwap(null);
    } else if (currentFieldIdToSwap === null) {
      setCurrentFieldIdToSwap(fieldIdOfFieldToSwap);
    }
  };

  const handleAddNewField = useCallback(
    (fieldType: EmailSignatureSocialLinkTypes) => {
      emailSignatureDispatch({
        type: 'ADD_SOCIAL_LINK',
        value: {
          id: generateNewEmailSignatureSocialLinkId(),
          type: fieldType,
          value: '',
          iconUrl: '',
        },
      });
    },
    [emailSignatureDispatch],
  );

  return (
    <EmailSignatureEditorSocialPanelContext.Provider
      value={{ isInSwapMode, currentFieldIdToSwap, onSwapActivate }}
    >
      <SideEditorPanel index={MenuItemsIndexDictionary[EmailSignatureSideEditorItems.SOCIAL]}>
        <EmailSignatureEditorSocialLinksSection />
        {!isEmpty(emailSignature.socialLinks) &&
          numSocialLinks < NUM_SOCIAL_LINKS_RECOMMENDATION && (
            <EmailSignatureEditorPanelNewFieldsSectionHelper>
              We recommend adding at least {NUM_SOCIAL_LINKS_RECOMMENDATION} links
            </EmailSignatureEditorPanelNewFieldsSectionHelper>
          )}
        <EmailSignatureEditorPanelNewFieldsSection>
          <EmailSignatureEditorNewFieldButton
            disabled={isSignatureLocked}
            onActivate={handleAddNewField}
            type={EmailSignatureSocialLinkTypes.LINKEDIN}
            subtitle={formatEmailSignatureSocialLinksType(EmailSignatureSocialLinkTypes.LINKEDIN)}
          />
          <EmailSignatureEditorNewFieldButton
            disabled={isSignatureLocked}
            onActivate={handleAddNewField}
            type={EmailSignatureSocialLinkTypes.CALENDLY}
            subtitle={formatEmailSignatureSocialLinksType(EmailSignatureSocialLinkTypes.CALENDLY)}
          />
          <EmailSignatureEditorNewFieldButton
            disabled={isSignatureLocked}
            onActivate={handleAddNewField}
            type={EmailSignatureSocialLinkTypes.FACEBOOK}
            subtitle={formatEmailSignatureSocialLinksType(EmailSignatureSocialLinkTypes.FACEBOOK)}
          />
          <EmailSignatureEditorNewFieldButton
            disabled={isSignatureLocked}
            onActivate={handleAddNewField}
            type={EmailSignatureSocialLinkTypes.INSTAGRAM}
            subtitle={formatEmailSignatureSocialLinksType(EmailSignatureSocialLinkTypes.INSTAGRAM)}
          />
          <EmailSignatureEditorNewFieldButton
            disabled={isSignatureLocked}
            onActivate={handleAddNewField}
            type={EmailSignatureSocialLinkTypes.YOUTUBE}
            subtitle={formatEmailSignatureSocialLinksType(EmailSignatureSocialLinkTypes.YOUTUBE)}
          />

          <EmailSignatureEditorNewFieldButton
            disabled={isSignatureLocked}
            onActivate={handleAddNewField}
            type={EmailSignatureSocialLinkTypes.TWITTER}
            subtitle={formatEmailSignatureSocialLinksType(EmailSignatureSocialLinkTypes.TWITTER)}
          />
          <EmailSignatureEditorNewFieldButton
            disabled={isSignatureLocked}
            onActivate={handleAddNewField}
            type={EmailSignatureSocialLinkTypes.TIKTOK}
            subtitle={formatEmailSignatureSocialLinksType(EmailSignatureSocialLinkTypes.TIKTOK)}
          />

          <EmailSignatureEditorNewFieldButton
            disabled={isSignatureLocked}
            onActivate={handleAddNewField}
            type={EmailSignatureSocialLinkTypes.SNAPCHAT}
            subtitle={formatEmailSignatureSocialLinksType(EmailSignatureSocialLinkTypes.SNAPCHAT)}
          />
          <EmailSignatureEditorNewFieldButton
            disabled={isSignatureLocked}
            onActivate={handleAddNewField}
            type={EmailSignatureSocialLinkTypes.YELP}
            subtitle={formatEmailSignatureSocialLinksType(EmailSignatureSocialLinkTypes.YELP)}
          />
          <EmailSignatureEditorNewFieldButton
            disabled={isSignatureLocked}
            onActivate={handleAddNewField}
            type={EmailSignatureSocialLinkTypes.TWITCH}
            subtitle={formatEmailSignatureSocialLinksType(EmailSignatureSocialLinkTypes.TWITCH)}
          />
          <EmailSignatureEditorNewFieldButton
            disabled={isSignatureLocked}
            onActivate={handleAddNewField}
            type={EmailSignatureSocialLinkTypes.DISCORD}
            subtitle={formatEmailSignatureSocialLinksType(EmailSignatureSocialLinkTypes.DISCORD)}
          />
          <EmailSignatureEditorNewFieldButton
            disabled={isSignatureLocked}
            onActivate={handleAddNewField}
            type={EmailSignatureSocialLinkTypes.GITHUB}
            subtitle={formatEmailSignatureSocialLinksType(EmailSignatureSocialLinkTypes.GITHUB)}
          />

          <EmailSignatureEditorNewFieldButton
            disabled={isSignatureLocked}
            onActivate={handleAddNewField}
            type={EmailSignatureSocialLinkTypes.PAYPAL}
            subtitle={formatEmailSignatureSocialLinksType(EmailSignatureSocialLinkTypes.PAYPAL)}
          />
          <EmailSignatureEditorNewFieldButton
            disabled={isSignatureLocked}
            onActivate={handleAddNewField}
            type={EmailSignatureSocialLinkTypes.VENMO}
            subtitle={formatEmailSignatureSocialLinksType(EmailSignatureSocialLinkTypes.VENMO)}
          />
          <EmailSignatureEditorNewFieldButton
            disabled={isSignatureLocked}
            onActivate={handleAddNewField}
            type={EmailSignatureSocialLinkTypes.CASHAPP}
            subtitle={formatEmailSignatureSocialLinksType(EmailSignatureSocialLinkTypes.CASHAPP)}
          />

          {isSignatureLocked && (
            <EmailSignatureLockLayer
              message={
                'You cannot add any extra links to your signature, please contact your admin if you have any questions'
              }
            />
          )}
        </EmailSignatureEditorPanelNewFieldsSection>
      </SideEditorPanel>
    </EmailSignatureEditorSocialPanelContext.Provider>
  );
};

export default EmailSignatureSideEditorSocialPanel;
